import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Stack,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import {
  Add,
  CheckCircle,
  Edit,
  Assignment,
  Tune,
  Delete as DeleteIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../constants/roles';
import { roleMatches } from '../../utils/rolePermissions';
import {
  DEFAULT_TECHNICAL_LOG_BOOK_DEFINITION,
  FIELD_TYPES,
  MAX_TECHNICAL_LOG_BOOK_SECTOR_SLOTS,
  DEFAULT_SECTORS_PER_TECHLOGBOOK,
  ensureHobbsMeterRecordsSection,
  ensureHobbsMeterRecordsTotalsFields,
  ensureIapCountSectorField
} from '../../constants/technicalLogBookDefinition';

const FORMAT_MANAGER_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ACCOUNTABLE_MANAGER,
  ROLES.CA_MANAGER,
  ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER
];

const normalizeSectorSection = (sectorSec) => {
  if (!sectorSec) return;
  if (!Number.isInteger(sectorSec.sectorSlots)) {
    sectorSec.sectorSlots = DEFAULT_SECTORS_PER_TECHLOGBOOK;
  } else {
    sectorSec.sectorSlots = Math.min(
      MAX_TECHNICAL_LOG_BOOK_SECTOR_SLOTS,
      Math.max(1, sectorSec.sectorSlots)
    );
  }
  if (!Array.isArray(sectorSec.customFieldSections)) sectorSec.customFieldSections = [];
  sectorSec.customFieldSections = ensureHobbsMeterRecordsSection(sectorSec.customFieldSections);
  sectorSec.fields = ensureIapCountSectorField(sectorSec.fields);
  if (sectorSec.customFieldSections.length === 0 && (sectorSec.customFields || []).length > 0) {
    sectorSec.customFieldSections = [{
      id: 'cfs-legacy',
      label: 'Sector custom fields',
      fields: sectorSec.customFields.map((f) => ({ ...f }))
    }];
  }
};

const normalizeTotalsSection = (totalsSec) => {
  if (!totalsSec) return;
  const normalized = ensureHobbsMeterRecordsTotalsFields(totalsSec);
  if (normalized?.fields) totalsSec.fields = normalized.fields;
};

const normalizeDefinitionSections = (definition) => {
  if (!definition?.sections) return;
  normalizeSectorSection(definition.sections.find((section) => section.id === 'sector'));
  normalizeTotalsSection(definition.sections.find((section) => section.id === 'totals'));
};

const TechnicalLogBookFormatManagement = () => {
  const [loading, setLoading] = useState(true);
  const [formats, setFormats] = useState([]);
  const [defaultFormat, setDefaultFormat] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [newVersion, setNewVersion] = useState('');
  const [newIsDefault, setNewIsDefault] = useState(false);
  const [editVersion, setEditVersion] = useState('');
  const [aircraft, setAircraft] = useState([]);
  const [selectedAircraftIds, setSelectedAircraftIds] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [usage, setUsage] = useState(null);
  const [defineDialogOpen, setDefineDialogOpen] = useState(false);
  const [defineFormat, setDefineFormat] = useState(null);
  const [defineDefinition, setDefineDefinition] = useState(null);
  const [savingDefinition, setSavingDefinition] = useState(false);
  const [defineSectionTab, setDefineSectionTab] = useState(0);
  const [definitionEditorMode, setDefinitionEditorMode] = useState('existing');
  const [createDefinition, setCreateDefinition] = useState(null);
  const [cloneSourceFormatId, setCloneSourceFormatId] = useState('');
  const { showToast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    if (!roleMatches(user?.role, FORMAT_MANAGER_ROLES)) {
      showToast('Access denied.', 'error');
      return;
    }
    fetchFormats();
    fetchDefaultFormat();
  }, [user]);

  const fetchFormats = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/technical-log-book-format/history');
      setFormats(response.data);
    } catch (error) {
      console.error('Error fetching format history:', error);
      showToast('Failed to load format history', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchDefaultFormat = async () => {
    try {
      const response = await axios.get('/api/technical-log-book-format/');
      setDefaultFormat(response.data);
    } catch (error) {
      console.error('Error fetching default format:', error);
    }
  };

  const handleCreate = async () => {
    if (!newVersion.trim()) {
      showToast('Version is required', 'error');
      return;
    }
    try {
      await axios.post('/api/technical-log-book-format/', {
        version: newVersion.trim(),
        isActive: true,
        isDefault: newIsDefault,
        definition: createDefinition || undefined
      });
      showToast('Format version created successfully', 'success');
      setCreateDialogOpen(false);
      setNewVersion('');
      setNewIsDefault(false);
      setCreateDefinition(null);
      setCloneSourceFormatId('');
      fetchFormats();
      fetchDefaultFormat();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to create format version', 'error');
    }
  };

  const handleEdit = async () => {
    if (!editVersion.trim()) {
      showToast('Version is required', 'error');
      return;
    }
    try {
      await axios.put(`/api/technical-log-book-format/${selectedFormat._id}`, {
        version: editVersion.trim()
      });
      showToast('Format version updated successfully', 'success');
      setEditDialogOpen(false);
      setSelectedFormat(null);
      setEditVersion('');
      fetchFormats();
      fetchDefaultFormat();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to update format version', 'error');
    }
  };

  const handleSetDefault = async (formatId) => {
    try {
      await axios.post(`/api/technical-log-book-format/${formatId}/set-default`);
      showToast('Default format set successfully', 'success');
      fetchFormats();
      fetchDefaultFormat();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to set default format', 'error');
    }
  };

  const handleActivate = async (formatId) => {
    try {
      await axios.post(`/api/technical-log-book-format/${formatId}/activate`);
      showToast('Format version activated successfully', 'success');
      fetchFormats();
      fetchDefaultFormat();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to activate format version', 'error');
    }
  };

  const handleEditClick = (format) => {
    setSelectedFormat(format);
    setEditVersion(format.version);
    setEditDialogOpen(true);
  };

  const openDefineDialog = (format) => {
    const def = JSON.parse(JSON.stringify(format.definition || DEFAULT_TECHNICAL_LOG_BOOK_DEFINITION));
    normalizeDefinitionSections(def);
    setDefineFormat(format);
    setDefineDefinition(def);
    setDefineSectionTab(0);
    setDefinitionEditorMode('existing');
    setDefineDialogOpen(true);
  };

  const openCreateDefinitionDialog = () => {
    const initialDefinition = JSON.parse(JSON.stringify(createDefinition || DEFAULT_TECHNICAL_LOG_BOOK_DEFINITION));
    normalizeDefinitionSections(initialDefinition);
    setDefineFormat(null);
    setDefineDefinition(initialDefinition);
    setDefineSectionTab(0);
    setDefinitionEditorMode('create');
    setDefineDialogOpen(true);
  };

  const handleClonePreviousFormats = () => {
    const sourceFormat = formats.find((format) => String(format._id) === String(cloneSourceFormatId)) || formats[0];
    if (!sourceFormat) {
      showToast('No previous formats available to clone', 'error');
      return;
    }
    const clonedDefinition = JSON.parse(JSON.stringify(sourceFormat.definition || DEFAULT_TECHNICAL_LOG_BOOK_DEFINITION));
    setCreateDefinition(clonedDefinition);
    setCloneSourceFormatId(sourceFormat._id);
    showToast(`Cloned format values from ${sourceFormat.version}`, 'success');
  };

  const handleRemoveClonedValues = () => {
    setCreateDefinition(null);
    setCloneSourceFormatId('');
  };

  const handleResetDefinition = () => {
    if (window.confirm('Reset to current Technical Log Book structure? This will replace all changes in this dialog.')) {
      setDefineDefinition(JSON.parse(JSON.stringify(DEFAULT_TECHNICAL_LOG_BOOK_DEFINITION)));
    }
  };

  const updateSection = (sectionIndex, updates) => {
    setDefineDefinition(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.sections[sectionIndex] = { ...next.sections[sectionIndex], ...updates };
      return next;
    });
  };

  const updateField = (sectionIndex, fieldIndex, updates) => {
    setDefineDefinition(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.sections[sectionIndex].fields[fieldIndex] = { ...next.sections[sectionIndex].fields[fieldIndex], ...updates };
      return next;
    });
  };

  const updateCustomField = (sectionIndex, customFieldIndex, updates) => {
    setDefineDefinition(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.sections[sectionIndex].customFields[customFieldIndex] = { ...next.sections[sectionIndex].customFields[customFieldIndex], ...updates };
      return next;
    });
  };

  const addCustomField = (sectionIndex) => {
    setDefineDefinition(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.sections[sectionIndex].customFields = next.sections[sectionIndex].customFields || [];
      next.sections[sectionIndex].customFields.push({ key: '', label: 'New field', type: 'text', visible: true, required: false, options: [] });
      return next;
    });
  };

  const removeCustomField = (sectionIndex, customFieldIndex) => {
    setDefineDefinition(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.sections[sectionIndex].customFields.splice(customFieldIndex, 1);
      return next;
    });
  };

  const addSectorCustomFieldGroup = (sectionIndex) => {
    setDefineDefinition((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const sec = next.sections[sectionIndex];
      if (sec.id !== 'sector') return next;
      sec.customFieldSections = sec.customFieldSections || [];
      sec.customFieldSections.push({
        id: `cfs-${Date.now()}`,
        label: 'Custom fields',
        fields: []
      });
      return next;
    });
  };

  const removeSectorCustomFieldGroup = (sectionIndex, groupIndex) => {
    setDefineDefinition((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const sec = next.sections[sectionIndex];
      if (sec.id !== 'sector' || !sec.customFieldSections) return next;
      sec.customFieldSections.splice(groupIndex, 1);
      return next;
    });
  };

  const updateSectorCustomFieldGroup = (sectionIndex, groupIndex, updates) => {
    setDefineDefinition((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const sec = next.sections[sectionIndex];
      if (sec.id !== 'sector' || !sec.customFieldSections?.[groupIndex]) return next;
      sec.customFieldSections[groupIndex] = { ...sec.customFieldSections[groupIndex], ...updates };
      return next;
    });
  };

  const addSectorCustomFieldInGroup = (sectionIndex, groupIndex) => {
    setDefineDefinition((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const sec = next.sections[sectionIndex];
      if (sec.id !== 'sector' || !sec.customFieldSections?.[groupIndex]) return next;
      sec.customFieldSections[groupIndex].fields = sec.customFieldSections[groupIndex].fields || [];
      sec.customFieldSections[groupIndex].fields.push({ key: '', label: 'New field', type: 'text', visible: true, required: false, options: [] });
      return next;
    });
  };

  const updateSectorCustomFieldInGroup = (sectionIndex, groupIndex, fieldIndex, updates) => {
    setDefineDefinition((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const sec = next.sections[sectionIndex];
      const fields = sec.customFieldSections?.[groupIndex]?.fields;
      if (sec.id !== 'sector' || !fields?.[fieldIndex]) return next;
      fields[fieldIndex] = { ...fields[fieldIndex], ...updates };
      return next;
    });
  };

  const removeSectorCustomFieldInGroup = (sectionIndex, groupIndex, fieldIndex) => {
    setDefineDefinition((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const sec = next.sections[sectionIndex];
      const fields = sec.customFieldSections?.[groupIndex]?.fields;
      if (sec.id !== 'sector' || !fields) return next;
      fields.splice(fieldIndex, 1);
      return next;
    });
  };

  const handleSaveDefinition = async () => {
    if (!defineFormat || !defineDefinition) return;
    if (definitionEditorMode === 'create') {
      const cloned = JSON.parse(JSON.stringify(defineDefinition));
      normalizeDefinitionSections(cloned);
      setCreateDefinition(cloned);
      setDefineDialogOpen(false);
      setDefineFormat(null);
      setDefineDefinition(null);
      showToast('Cloned format values updated', 'success');
      return;
    }
    setSavingDefinition(true);
    try {
      const definitionPayload = JSON.parse(JSON.stringify(defineDefinition));
      normalizeDefinitionSections(definitionPayload);
      const sectorSec = definitionPayload.sections?.find((s) => s.id === 'sector');
      if (sectorSec) {
        if (Array.isArray(sectorSec.customFieldSections)) {
          sectorSec.customFields = sectorSec.customFieldSections.flatMap((g) => g.fields || []);
        }
      }
      await axios.put(`/api/technical-log-book-format/${defineFormat._id}`, { definition: definitionPayload });
      showToast('Log book definition saved', 'success');
      setDefineDialogOpen(false);
      setDefineFormat(null);
      setDefineDefinition(null);
      fetchFormats();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to save definition', 'error');
    } finally {
      setSavingDefinition(false);
    }
  };

  const openAssignDialog = async (format) => {
    setSelectedFormat(format);
    setSelectedAircraftIds([]);
    setAssignDialogOpen(true);
    try {
      const [aircraftRes, usageRes] = await Promise.all([
        axios.get('/api/aircraft'),
        axios.get(`/api/technical-log-book-format/${format._id}/usage`).catch(() => ({ data: { aircraftCount: 0, technicalLogBookEntryCount: 0 } }))
      ]);
      const list = aircraftRes.data || [];
      setAircraft(list);
      setUsage(usageRes.data || null);
      // Pre-check aircraft that are already assigned to this format
      const formatIdStr = String(format._id);
      const alreadyAssignedIds = list
        .filter((ac) => {
          const fid = ac.approvedTechnicalLogBookFormatId?._id ?? ac.approvedTechnicalLogBookFormatId;
          return fid && String(fid) === formatIdStr;
        })
        .map((ac) => ac._id);
      setSelectedAircraftIds(alreadyAssignedIds);
    } catch (e) {
      showToast('Failed to load aircraft list', 'error');
      setAircraft([]);
      setUsage(null);
    }
  };

  const toggleAircraftSelection = (id) => {
    setSelectedAircraftIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleAssignAircraft = async () => {
    if (!selectedFormat) return;
    setAssigning(true);
    try {
      const res = await axios.post(`/api/technical-log-book-format/${selectedFormat._id}/assign-aircraft`, {
        aircraftIds: selectedAircraftIds
      });
      const { assigned = 0, unassigned = 0, aircraftWithExistingEntries } = res.data || {};
      if (assigned > 0 && aircraftWithExistingEntries > 0) {
        showToast(
          `${assigned} aircraft assigned. ${aircraftWithExistingEntries} of them have existing Technical Log Book entries; new entries will use this format.`,
          'success'
        );
      } else if (assigned > 0) {
        showToast(`Format assigned to ${assigned} aircraft`, 'success');
      } else if (unassigned > 0) {
        showToast(`${unassigned} aircraft unassigned from this format`, 'success');
      } else {
        showToast('Assignment updated', 'success');
      }
      setAssignDialogOpen(false);
      setSelectedFormat(null);
      setSelectedAircraftIds([]);
      fetchFormats();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to update assignment', 'error');
    } finally {
      setAssigning(false);
    }
  };

  if (!roleMatches(user?.role, FORMAT_MANAGER_ROLES)) {
    return (
      <Box>
        <Alert severity="error">Access denied.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4">Technical Log Book Format Management</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage Issue Format Revision versions for Technical Log Book
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create New Version
        </Button>
      </Box>

      {defaultFormat && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Default Format (for new/unassigned aircraft)
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip label={defaultFormat.version} color="success" />
            <Typography variant="body2" color="text.secondary">
              Created: {dayjs(defaultFormat.createdAt).format('DD MMM YYYY')} by {defaultFormat.createdBy?.firstName} {defaultFormat.createdBy?.lastName}
            </Typography>
          </Stack>
        </Paper>
      )}

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Format Version History
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formats.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No format versions found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                formats.map((format) => (
                  <TableRow key={format._id}>
                    <TableCell>{format.version}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {format.isDefault && (
                          <Chip label="Default" color="success" size="small" />
                        )}
                        {format.isActive && !format.isDefault && (
                          <Chip label="Active" color="primary" size="small" variant="outlined" />
                        )}
                        {!format.isActive && (
                          <Chip label="Inactive" color="default" size="small" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {format.createdBy?.firstName} {format.createdBy?.lastName}
                    </TableCell>
                    <TableCell>
                      {dayjs(format.createdAt).format('DD MMM YYYY, HH:mm')}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(format)}
                        color="primary"
                        title="Edit"
                      >
                        <Edit />
                      </IconButton>
                      {!format.isDefault && (
                        <IconButton
                          size="small"
                          onClick={() => handleSetDefault(format._id)}
                          color="success"
                          title="Set as default"
                        >
                          <CheckCircle />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => openAssignDialog(format)}
                        color="primary"
                        title="Assign to aircraft"
                      >
                        <Assignment />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => openDefineDialog(format)}
                        color="primary"
                        title="Define log book"
                      >
                        <Tune />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setCreateDefinition(null);
          setCloneSourceFormatId('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Format Version</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Version"
            value={newVersion}
            onChange={(e) => setNewVersion(e.target.value)}
            placeholder="e.g., Issue 01 Rev 02 Dtd Jan 2025"
            sx={{ mt: 2 }}
            helperText="Enter the format version identifier"
          />
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
            <input
              type="checkbox"
              id="newIsDefault"
              checked={newIsDefault}
              onChange={(e) => setNewIsDefault(e.target.checked)}
            />
            <label htmlFor="newIsDefault" style={{ marginLeft: 8 }}>
              Set as default for new aircraft
            </label>
          </Box>
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Clone previous formats</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <FormControl size="small" fullWidth>
                <Select
                  displayEmpty
                  value={cloneSourceFormatId}
                  onChange={(e) => setCloneSourceFormatId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>{formats.length > 0 ? 'Select previous version' : 'No versions available'}</em>
                  </MenuItem>
                  {formats.map((format) => (
                    <MenuItem key={format._id} value={format._id}>
                      {format.version}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                onClick={handleClonePreviousFormats}
                disabled={formats.length === 0 || !cloneSourceFormatId}
              >
                Clone Previous Formats
              </Button>
            </Stack>
            {createDefinition && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
                <Button size="small" onClick={openCreateDefinitionDialog}>
                  Edit cloned values
                </Button>
                <Button size="small" color="error" onClick={handleRemoveClonedValues}>
                  Remove cloned values
                </Button>
              </Stack>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateDialogOpen(false);
              setCreateDefinition(null);
              setCloneSourceFormatId('');
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={!newVersion.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Format Version</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Version"
            value={editVersion}
            onChange={(e) => setEditVersion(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit} disabled={!editVersion.trim()}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign to aircraft Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => { setAssignDialogOpen(false); setSelectedFormat(null); setSelectedAircraftIds([]); }} maxWidth="sm" fullWidth>
        <DialogTitle>Assign format to aircraft</DialogTitle>
        <DialogContent>
          {selectedFormat && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Format: <strong>{selectedFormat.version}</strong>
              {usage && usage.aircraftCount > 0 && (
                <> — Used by {usage.aircraftCount} aircraft</>
              )}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mb: 1 }}>
            Select aircraft that should use this format. Checked = assigned; uncheck to unassign. Save to update.
          </Typography>
          <Box sx={{ maxHeight: 320, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
            {aircraft.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No aircraft found</Typography>
            ) : (
              aircraft.map((ac) => (
                <Box key={ac._id} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                  <input
                    type="checkbox"
                    id={`aircraft-${ac._id}`}
                    checked={selectedAircraftIds.includes(ac._id)}
                    onChange={() => toggleAircraftSelection(ac._id)}
                  />
                  <label htmlFor={`aircraft-${ac._id}`} style={{ marginLeft: 8, cursor: 'pointer' }}>
                    {ac.registration} — {ac.manufacturer} {ac.model}
                  </label>
                </Box>
              ))
            )}
          </Box>
          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
            Changing format affects only new Technical Log Book entries; existing entries keep their recorded format.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAssignDialogOpen(false); setSelectedFormat(null); setSelectedAircraftIds([]); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignAircraft}
            disabled={assigning}
          >
            {assigning ? 'Updating…' : selectedAircraftIds.length === 0 ? 'Update (unassign all)' : `Update assignment (${selectedAircraftIds.length} aircraft)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Define log book Dialog */}
      <Dialog
        open={defineDialogOpen}
        onClose={() => { setDefineDialogOpen(false); setDefineFormat(null); setDefineDefinition(null); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {definitionEditorMode === 'create'
            ? 'Edit cloned format values'
            : `Define log book ${defineFormat ? `— ${defineFormat.version}` : ''}`}
        </DialogTitle>
        <DialogContent>
          {defineDefinition && (
            <>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Tabs value={defineSectionTab} onChange={(_, v) => setDefineSectionTab(v)}>
                  {defineDefinition.sections.map((sec, i) => (
                    <Tab key={sec.id} label={sec.label} id={`define-tab-${i}`} />
                  ))}
                </Tabs>
                <Button size="small" variant="outlined" color="secondary" onClick={handleResetDefinition}>
                  Reset to current TLB structure
                </Button>
              </Stack>
              {defineDefinition.sections.map((section, sectionIndex) => (
                <Box
                  key={section.id}
                  role="tabpanel"
                  hidden={defineSectionTab !== sectionIndex}
                  id={`define-tabpanel-${sectionIndex}`}
                  sx={{ pt: 1 }}
                >
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <TextField
                      size="small"
                      label="Section label"
                      value={section.label}
                      onChange={(e) => updateSection(sectionIndex, { label: e.target.value })}
                      sx={{ minWidth: 180 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={section.visible !== false}
                          onChange={(e) => updateSection(sectionIndex, { visible: e.target.checked })}
                        />
                      }
                      label="Section visible"
                    />
                  </Stack>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Built-in fields</Typography>
                  {section.id === 'totals' && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      HOBBS METER RECORDS totals fields (<code>totalBroughtForward.hobbs</code>, <code>totalBroughtForward.hobbsAC</code>, and carried-forward pairs) roll up sector hobbs readings. Field labels are editable per aircraft format.
                    </Typography>
                  )}
                  {section.id === 'sector' && (
                    <Box sx={{ mb: 2 }}>
                      <TextField
                        size="small"
                        type="number"
                        label="Maximum sectors per sheet"
                        value={section.sectorSlots ?? DEFAULT_SECTORS_PER_TECHLOGBOOK}
                        onChange={(e) => {
                          const parsed = parseInt(e.target.value, 10);
                          const sectorSlots = Number.isInteger(parsed)
                            ? Math.min(MAX_TECHNICAL_LOG_BOOK_SECTOR_SLOTS, Math.max(1, parsed))
                            : DEFAULT_SECTORS_PER_TECHLOGBOOK;
                          updateSection(sectionIndex, { sectorSlots });
                        }}
                        inputProps={{ min: 1, max: MAX_TECHNICAL_LOG_BOOK_SECTOR_SLOTS, step: 1 }}
                        helperText={`How many sectors fit on one tech log book sheet (1–${MAX_TECHNICAL_LOG_BOOK_SECTOR_SLOTS}). Additional sectors open a new sheet.`}
                        sx={{ maxWidth: 320 }}
                      />
                    </Box>
                  )}
                  <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: '40%', maxWidth: 400 }}>Path / Key</TableCell>
                          <TableCell sx={{ width: '30%', minWidth: 220 }}>Label</TableCell>
                          <TableCell sx={{ width: '14%', minWidth: 110 }}>Type</TableCell>
                          <TableCell sx={{ width: '6%', textAlign: 'center' }}>Visible</TableCell>
                          <TableCell sx={{ width: '6%', textAlign: 'center' }}>Required</TableCell>
                          <TableCell sx={{ width: '6%', textAlign: 'center' }}>Read-only</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(section.fields || []).map((field, fieldIndex) => (
                          <TableRow key={field.path || field.key || fieldIndex}>
                            <TableCell
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.85rem',
                                width: '40%',
                                maxWidth: 400,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                              title={field.path || field.key || '—'}
                            >
                              {field.path || field.key || '—'}
                            </TableCell>
                            <TableCell sx={{ width: '40%', minWidth: 260 }}>
                              <TextField
                                size="small"
                                fullWidth
                                value={field.label || ''}
                                onChange={(e) => updateField(sectionIndex, fieldIndex, { label: e.target.value })}
                              />
                            </TableCell>
                            <TableCell sx={{ width: '14%', minWidth: 110 }}>
                              <FormControl size="small" fullWidth>
                                <Select
                                  value={field.type || 'text'}
                                  onChange={(e) => updateField(sectionIndex, fieldIndex, { type: e.target.value })}
                                >
                                  {FIELD_TYPES.map((t) => (
                                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell sx={{ width: '6%', textAlign: 'center' }}>
                              <Checkbox
                                checked={field.visible !== false}
                                onChange={(e) => updateField(sectionIndex, fieldIndex, { visible: e.target.checked })}
                                size="small"
                              />
                            </TableCell>
                            <TableCell sx={{ width: '6%', textAlign: 'center' }}>
                              <Checkbox
                                checked={!!field.required}
                                onChange={(e) => updateField(sectionIndex, fieldIndex, { required: e.target.checked })}
                                size="small"
                              />
                            </TableCell>
                            <TableCell sx={{ width: '6%', textAlign: 'center' }}>
                              <Checkbox
                                checked={!!field.readOnly}
                                onChange={(e) => updateField(sectionIndex, fieldIndex, { readOnly: e.target.checked })}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Custom fields</Typography>
                  {section.id === 'sector' ? (
                    <>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Add one or more groups; each group appears as its own titled block on every sector tab. Field keys must stay unique across all groups. Group titles and field labels can be customized per aircraft format (rename &quot;HOBBS METER RECORDS&quot; or its fields for each assigned aircraft).
                      </Typography>
                      <Box sx={{ mb: 1 }}>
                        <Button size="small" startIcon={<Add />} onClick={() => addSectorCustomFieldGroup(sectionIndex)}>
                          Add custom field group
                        </Button>
                      </Box>
                      {(section.customFieldSections || []).map((grp, gi) => (
                        <Paper key={grp.id || gi} variant="outlined" sx={{ p: 2, mb: 2 }}>
                          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                            <TextField
                              size="small"
                              label="Group title"
                              value={grp.label || ''}
                              onChange={(e) => updateSectorCustomFieldGroup(sectionIndex, gi, { label: e.target.value })}
                              sx={{ minWidth: 220, flex: 1 }}
                            />
                            <IconButton size="small" color="error" onClick={() => removeSectorCustomFieldGroup(sectionIndex, gi)} title="Remove group">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                          <Box sx={{ mb: 1 }}>
                            <Button size="small" startIcon={<Add />} onClick={() => addSectorCustomFieldInGroup(sectionIndex, gi)}>
                              Add field in this group
                            </Button>
                          </Box>
                          {(grp.fields || []).length > 0 && (
                            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ width: '25%', maxWidth: 220 }}>Key</TableCell>
                                    <TableCell sx={{ width: '45%', minWidth: 220 }}>Label</TableCell>
                                    <TableCell sx={{ width: '16%', minWidth: 110 }}>Type</TableCell>
                                    <TableCell sx={{ width: '7%', textAlign: 'center' }}>Visible</TableCell>
                                    <TableCell sx={{ width: '7%', textAlign: 'center' }}>Required</TableCell>
                                    <TableCell align="right" sx={{ width: '5%' }} />
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(grp.fields || []).map((cf, cfIndex) => (
                                    <TableRow key={cfIndex}>
                                      <TableCell sx={{ width: '25%', maxWidth: 220 }}>
                                        <TextField
                                          size="small"
                                          placeholder="e.g. customKey1"
                                          value={cf.key || ''}
                                          onChange={(e) => updateSectorCustomFieldInGroup(sectionIndex, gi, cfIndex, { key: e.target.value })}
                                          fullWidth
                                        />
                                      </TableCell>
                                      <TableCell sx={{ width: '45%', minWidth: 220 }}>
                                        <TextField
                                          size="small"
                                          value={cf.label || ''}
                                          onChange={(e) => updateSectorCustomFieldInGroup(sectionIndex, gi, cfIndex, { label: e.target.value })}
                                          fullWidth
                                        />
                                      </TableCell>
                                      <TableCell sx={{ width: '16%', minWidth: 110 }}>
                                        <FormControl size="small" fullWidth>
                                          <Select
                                            value={cf.type || 'text'}
                                            onChange={(e) => updateSectorCustomFieldInGroup(sectionIndex, gi, cfIndex, { type: e.target.value })}
                                          >
                                            {FIELD_TYPES.map((t) => (
                                              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                                            ))}
                                          </Select>
                                        </FormControl>
                                      </TableCell>
                                      <TableCell sx={{ width: '7%', textAlign: 'center' }}>
                                        <Checkbox
                                          checked={cf.visible !== false}
                                          onChange={(e) => updateSectorCustomFieldInGroup(sectionIndex, gi, cfIndex, { visible: e.target.checked })}
                                          size="small"
                                        />
                                      </TableCell>
                                      <TableCell sx={{ width: '7%', textAlign: 'center' }}>
                                        <Checkbox
                                          checked={!!cf.required}
                                          onChange={(e) => updateSectorCustomFieldInGroup(sectionIndex, gi, cfIndex, { required: e.target.checked })}
                                          size="small"
                                        />
                                      </TableCell>
                                      <TableCell sx={{ width: '5%', textAlign: 'right' }}>
                                        <IconButton size="small" onClick={() => removeSectorCustomFieldInGroup(sectionIndex, gi, cfIndex)} title="Remove">
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                        </Paper>
                      ))}
                    </>
                  ) : (
                    <>
                      <Box sx={{ mb: 1 }}>
                        <Button size="small" startIcon={<Add />} onClick={() => addCustomField(sectionIndex)}>
                          Add custom field
                        </Button>
                      </Box>
                      {(section.customFields || []).length > 0 && (
                        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ width: '25%', maxWidth: 220 }}>Key</TableCell>
                                <TableCell sx={{ width: '45%', minWidth: 220 }}>Label</TableCell>
                                <TableCell sx={{ width: '16%', minWidth: 110 }}>Type</TableCell>
                                <TableCell sx={{ width: '7%', textAlign: 'center' }}>Visible</TableCell>
                                <TableCell sx={{ width: '7%', textAlign: 'center' }}>Required</TableCell>
                                <TableCell align="right" sx={{ width: '5%' }} />
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(section.customFields || []).map((cf, cfIndex) => (
                                <TableRow key={cfIndex}>
                                  <TableCell sx={{ width: '25%', maxWidth: 220 }}>
                                    <TextField
                                      size="small"
                                      placeholder="e.g. customKey1"
                                      value={cf.key || ''}
                                      onChange={(e) => updateCustomField(sectionIndex, cfIndex, { key: e.target.value })}
                                      fullWidth
                                    />
                                  </TableCell>
                                  <TableCell sx={{ width: '45%', minWidth: 220 }}>
                                    <TextField
                                      size="small"
                                      value={cf.label || ''}
                                      onChange={(e) => updateCustomField(sectionIndex, cfIndex, { label: e.target.value })}
                                      fullWidth
                                    />
                                  </TableCell>
                                  <TableCell sx={{ width: '16%', minWidth: 110 }}>
                                    <FormControl size="small" fullWidth>
                                      <Select
                                        value={cf.type || 'text'}
                                        onChange={(e) => updateCustomField(sectionIndex, cfIndex, { type: e.target.value })}
                                      >
                                        {FIELD_TYPES.map((t) => (
                                          <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </TableCell>
                                  <TableCell sx={{ width: '7%', textAlign: 'center' }}>
                                    <Checkbox
                                      checked={cf.visible !== false}
                                      onChange={(e) => updateCustomField(sectionIndex, cfIndex, { visible: e.target.checked })}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell sx={{ width: '7%', textAlign: 'center' }}>
                                    <Checkbox
                                      checked={!!cf.required}
                                      onChange={(e) => updateCustomField(sectionIndex, cfIndex, { required: e.target.checked })}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell sx={{ width: '5%', textAlign: 'right' }}>
                                    <IconButton size="small" onClick={() => removeCustomField(sectionIndex, cfIndex)} title="Remove">
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </>
                  )}
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDefineDialogOpen(false); setDefineFormat(null); setDefineDefinition(null); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveDefinition}
            disabled={(definitionEditorMode === 'existing' && savingDefinition) || !defineDefinition || (definitionEditorMode === 'existing' && !defineFormat)}
          >
            {definitionEditorMode === 'create' ? 'Save cloned values' : (savingDefinition ? 'Saving…' : 'Save definition')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TechnicalLogBookFormatManagement;
